import { dateFns, dateTimeFns, Iso, zonedDateTimeFns } from 'iso-fns2'
import { isoZ, z } from 'my-zod'
import { sanitizePhoneNumberForSensibleWeather } from './sanitize-phone-number-for-sensible-weather'

const exposureProtections = z.object({
  name: z.string(),
  upperThreshold: z.number(),
  unit: z.string()
})

const payoutTiers = z.object({
  numberOfHours: z.number(),
  payoutAmount: z.number()
})

const plainLanguage = z.object({
  details: z.object({
    action: z.string(),
    close: z.string(),
    disclaimer: z.string(),
    step1Body: z.string(),
    step1Heading: z.string(),
    step2Body: z.string(),
    step2Heading: z.string(),
    step3Body: z.string(),
    step3Heading: z.string(),
    stepsHeading: z.string(),
    suggestedPrice: z.string(),
    suggestedPriceTotal: z.string(),
    summaryBody: z.string(),
    summaryBodyAltUnit: z.string(),
    summaryExplainer: z.string(),
    summaryHeading: z.string(),
    summarySubheading: z.string(),
    summaryTiers: z.array(z.string()),
    summaryTimes: z.string(),
    title: z.string()
  }),
  documents: z.object({
    privacyPolicy: z.object({
      link: z.string().url(),
      title: z.string()
    }),
    termsConditions: z.object({
      link: z.string().url(),
      title: z.string()
    })
  }),
  main: z.object({
    action: z.string(),
    body: z.string(),
    detailsAction: z.string(),
    disclaimer: z.string(),
    openDetails: z.string(),
    suggestedPrice: z.string(),
    suggestedPriceTotal: z.string(),
    title: z.string()
  })
})

// const documents = z.object({
//   name: z.string(),
//   link: z.string().url()
// })

const weatherGuaranteeBase = z.object({
  id: z.string(),
  coverageStartInstant: isoZ.instant(),
  coverageStartDateTime: isoZ.dateTime(),
  coverageEndDateTime: isoZ.dateTime(),
  price: z.number(),
  totalCoverageAmount: z.number(),
  exposureProtections: z.array(exposureProtections),
  payoutTiers: z.array(payoutTiers),
  createdDate: isoZ.instant()
})

const weatherGuaranteeQuote = weatherGuaranteeBase.augment({
  plainLanguage: plainLanguage,
  guaranteeId: z.string().nullable(),
  acceptedDate: isoZ.instant().nullable(),
  declinedDate: isoZ.instant().nullable()
})

const weatherGuarantee = weatherGuaranteeBase.augment({
  cancellationDate: isoZ.instant().nullable()
})

const createWeatherGuaranteeQuoteRequest = z.object({
  startDateTime: isoZ.dateTime(),
  endDateTime: isoZ.dateTime(),
  productId: z.string(),
  productVersionId: z.number(),
  totalCoverageAmount: z.number()
})

const createWeatherGuaranteeQuoteResponse = z
  .object({
    success: z.literal(true),
    data: weatherGuaranteeQuote
  })
  .or(
    z.object({
      success: z.literal(false),
      error: z.any()
    })
  )

function isWeatherGuaranteeAvailable({
  guarantee,
  now
}: {
  guarantee: {
    coverageStartDateTime: Iso.DateTime
  }
  now: Iso.ZonedDateTime
}) {
  return dateFns.isAfter(dateTimeFns.toDate(guarantee.coverageStartDateTime), zonedDateTimeFns.toDate(now))
}

function isWeatherGuaranteeCancellationAvailable({
  guarantee,
  now
}: {
  guarantee: {
    coverageStartDateTime: Iso.DateTime
  }
  now: Iso.ZonedDateTime
}) {
  return dateFns.isAfter(dateTimeFns.toDate(guarantee.coverageStartDateTime), zonedDateTimeFns.toDate(now))
}

export const SensibleWeatherSchemas = {
  createWeatherGuaranteeQuoteRequest,
  createWeatherGuaranteeQuoteResponse,
  weatherGuaranteeQuote,
  weatherGuarantee,
  isWeatherGuaranteeAvailable,
  isWeatherGuaranteeCancellationAvailable,
  sanitizePhoneNumberForSensibleWeather
}

export declare namespace SensibleWeatherSchemas {
  export type createWeatherGuaranteeQuoteRequest = z.infer<typeof createWeatherGuaranteeQuoteRequest>
  export type createWeatherGuaranteeQuoteResponse = z.infer<typeof createWeatherGuaranteeQuoteResponse>
  export type weatherGuaranteeQuote = z.infer<typeof weatherGuaranteeQuote>
  export type weatherGuarantee = z.infer<typeof weatherGuarantee>
}
